import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchRoles, createUserThunk, updateUserThunk } from "../../redux/slices/userSlice";

const EMPTY = {
  fullName: "",
  username: "",
  email: "",
  password: "",
  roleId: "",
  status: "Active",
};

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const UserModal = ({ show, onClose, editRecord }) => {
  const dispatch = useDispatch();
  const roles = useSelector((s) => s.users.roles);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const roleOptions = roles.map((r) => ({ value: r.roleID ?? r.id, label: r.roleName }));

  useEffect(() => { dispatch(fetchRoles()); }, [dispatch]);

  useEffect(() => {
    setForm(editRecord ? { ...EMPTY, ...editRecord, password: "" } : { ...EMPTY });
  }, [editRecord, show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.username) {
      Swal.fire("Required", "Full name, username and email are required.", "warning");
      return;
    }
    if (!editRecord && !form.password) {
      Swal.fire("Required", "Password is required for new users.", "warning");
      return;
    }
    setSaving(true);
    try {
      const recordId = editRecord?.userID ?? editRecord?.id;
      const payload = {
        fullName: form.fullName,
        username: form.username,
        email: form.email,
        roleId: form.roleId ? parseInt(form.roleId, 10) : null,
        status: form.status,
        ...(recordId
          ? (form.password ? { password: form.password } : {})
          : { initialPassword: form.password }),
      };
      if (recordId) {
        await dispatch(updateUserThunk({ id: recordId, data: payload })).unwrap();
      } else {
        await dispatch(createUserThunk(payload)).unwrap();
      }
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "User saved", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      const msg =
        (typeof err === "string" ? err : null) ||
        err?.message ||
        err?.error ||
        err?.title ||
        (err?.errors ? Object.values(err.errors).flat().join(", ") : null) ||
        JSON.stringify(err) ||
        "Failed to save user.";
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{editRecord ? "Edit User" : "Add User"}</h4>
              </div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Full Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.fullName} onChange={set("fullName")} placeholder="e.g. Dr. John Smith" autoComplete="off" autoCapitalize="words" spellCheck={false} style={{ textTransform: "none" }} />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Username <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.username} onChange={set("username")} placeholder="login username" autoComplete="off" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Email <span className="text-danger">*</span></label>
                    <input type="email" className="form-control" value={form.email} onChange={set("email")} placeholder="user@clinic.com" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      Password {!editRecord && <span className="text-danger">*</span>}
                      {editRecord && <span className="text-muted small ms-1">(leave blank to keep current)</span>}
                    </label>
                    <div className="pass-group">
                      <input
                        type={showPass ? "text" : "password"}
                        className="pass-input form-control"
                        value={form.password}
                        onChange={set("password")}
                        autoComplete="new-password"
                        placeholder="••••••••"
                      />
                      <span
                        className={`fas toggle-password ${showPass ? "fa-eye" : "fa-eye-slash"}`}
                        onClick={() => setShowPass((v) => !v)}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Role</label>
                    <Select
                      classNamePrefix="react-select"
                      options={roleOptions}
                      value={roleOptions.find((o) => o.value === form.roleId) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, roleId: opt?.value || "" }))}
                      placeholder="Assign role"
                      isClearable
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Status</label>
                    <Select
                      classNamePrefix="react-select"
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find((o) => o.value === form.status) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, status: opt?.value || "Active" }))}
                    />
                  </div>
                </div>
                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : editRecord ? "Save Changes" : "Add User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default UserModal;
